import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import PageBuilderLayout from '~/components/report/layouts/PageBuilderLayout'
import PDFDownloadLayout from '~/components/report/layouts/PDFDownloadLayout'
import ReportGate from '~/components/report/layouts/ReportGate'
import SimpleReportLayout from '~/components/report/layouts/SimpleReportLayout'
import type { PageData } from '~/components/report/types'
import { useAuthV2 } from '~/contexts/AuthContext'
import {
  artworkCard,
  ctas,
  getReportImageLink,
  image,
  internationalizedStringField,
  leadImage,
  myPortfolioBanner,
} from '~/schemas/common'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { metaQuery } from '~/utils/seo'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import {
  resolveUrlLinksWithTranslation,
  slugFromContext,
  urlQuery,
} from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'
const Report: NextPage<PageData> = (props: PageData) => {
  const { user } = useAuthV2()

  if (!props._id) return null

  if (!user) {
    return <ReportGate {...props} isLoggedIn={false} />
  }

  if (props.isPdfType) {
    return <PDFDownloadLayout {...props} />
  }

  if (props.isSimpleLayout) {
    return <SimpleReportLayout {...props} />
  }

  return <PageBuilderLayout {...props} />
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: { settings: any }
): Promise<GetStaticPropsResult<any>> {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity()

    const slug = slugFromContext(context)
    const lang = (context?.params?.lang as Locale) || 'en'
    const messages = await loadMessages(lang)

    const link = `"link": {
      "externalLink": linkExternal,
      "internalLink": link->{
        ${urlQuery},
      },
    }`

    const innerBody = `defined(body) => {
      "body": body[]${resolveUrlLinksWithTranslation(lang)} {
        ...,
        ${getReportImageLink()},
        ${link}
      },
    }`

    const data = await client.fetch(
      `*[_type == "report" && slug.current == $slug][0] {
        _id,
        ${metaQuery('report')},
        title,
        subtitle,
        summary,
        slug,
        "isPdfType": pdfDownload,
        "isSimpleLayout": simpleLayout,
        publishedDate,
        premiumBanner {
          title,
          description,
          bullets
        },
        "pdfFile": pdfFile.asset->{
          url,
          size
        },
        defined(pdfDownload) && pdfDownload == true => {
          "video": video->{
            videoUrl,
            watchTime,
            title,
            coverImage
          },
          introTitle,
          introContent,
          "secondaryIntroContent": secondaryIntroContent[] {
            title,
            content
          },
        },
        ${leadImage},
        ${myPortfolioBanner}
        defined(feedRelatedArtists) => {
          "relatedArtists": feedRelatedArtists[]->{
            "slug": slug.current,
            name
          },
        },
        (!defined(pdfDownload) || pdfDownload == false) && simpleLayout != true => {
        themeAccent,
        themeAccentText,
        navCenterTitle,
        mainNavTheme,
        navSlideoutTheme,
        inPageNav,
        inPageNavTheme,
        "advert": {
          "modalImage": advert.modalImage,
          "title": advert.title,
          "introduction": advert.introduction,
          "image": advert.image,
          "landscapeImage": advert.landscapeImage,
          "position": advert.position,
          "link": advert.link
        },
        "intro": intro[] {
          ...,
          defined(teamMembers) => {
            "teamMembers": teamMembers[]-> {
              name,
              "jobTitle": ${internationalizedStringField('jobTitle')},
              email,
              ${image},
              "slug": slug.current
            }
          },
          ${getReportImageLink()},
          defined(reportAuthor) => {
            "reportAuthor": reportAuthor-> {
              name,
              "slug": slug.current
            }
          },
          defined(reportEditor) => {
            "reportEditor": reportEditor-> {
              name,
              "slug": slug.current
            }
          },
          ${link},
          defined(teamMember) => {
            "teamMember": teamMember-> {
              name,
              "jobTitle": ${internationalizedStringField('jobTitle')},
              email,
              ${image},
              "slug": slug.current
            }
          },
          ${innerBody},
          ${ctas},
        },
        "body": body[]${resolveUrlLinksWithTranslation(lang)} {
          ...,
          ${ctas},
          ${link},
          ${getReportImageLink()},
          defined(teamMembers) => {
            "teamMembers": teamMembers[]-> {
              name,
              "jobTitle": ${internationalizedStringField('jobTitle')},
              email,
              ${image},
              "slug": slug.current
            }
          },
          defined(reportArtworkTable) => {
            "reportArtworkTable": reportArtworkTable->{
              ...,
            }
          },
          ${innerBody},
          defined(items) => {
            "items": items[]${resolveUrlLinksWithTranslation(lang)} {
              ...,
              ${ctas},
              ${getReportImageLink()},
              ${link},
              ${innerBody}
            },
          },
          defined(slides) => {
            "slides": slides[]${resolveUrlLinksWithTranslation(lang)} {
              ...,
              ${getReportImageLink()},
            },
          },
          defined(teamMember) => {
            "teamMember": teamMember-> {
              name,
              "jobTitle": ${internationalizedStringField('jobTitle')},
              email,
              "slug": slug.current
            }
          },
          _type == 'reportArtworkTable'=>{
            "items": items[]{
              ...,
              "artwork": artwork-> {
                ${artworkCard}
              }
            }
          },
        },
        },
        (!defined(pdfDownload) || pdfDownload == false) && simpleLayout == true => {
          "simpleBody": simpleBody[]${resolveUrlLinksWithTranslation(lang)} {
            ...,
            body[]${resolveUrlLinksWithTranslation(lang)},
            ${image},
            skipLinkTitle,
            omitSkipLink,
            isSkipLinkChild,
            moduleId,
            ${link},
            _type == "magazineImage" && link->_type == "artwork" && link->isVisible == true && link->_isDeleted != true => {
              "artwork": link->{
                ${urlQuery},
                title
              }
            },
            _type == "artworkImage" => {
              "image": {
                ...artwork->image,
                "text": altText,
                "alt": altText,
                "credit": {
                  "theme": imageCreditColor,
                  "text": imageCredit
                },
                "objectFit": objectFit,
              },
              "defaultCaption": {
                "artistName": artwork->artist->name,
                "artworkDate": artwork->year,
                "artworkTitle": artwork->title,
              },
              "artwork": artwork-> {
                ${urlQuery},
                title
              }
            },
          },
        },
        "primaryCategory": primaryCategory->{
          "slug": slug.current,
          "name": name[_key==$lang][0].value
        },
      }`,
      {
        slug: slug,
        lang: lang,
      }
    )

    const breadcrumbPieces = ['reports', data.title]
    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      breadcrumbPieces,
      '',
      undefined,
      messages,
      lang
    )

    // Use centralized SEO helper with locale-aware templates
    const metaProps = { meta: data.meta, _id: data._id }
    await resolvePageSeoMeta(context, metaProps, 'page')
    data.meta = metaProps.meta

    return {
      props: {
        url: `/${slug}`,
        ...data,
        hideHeader: false,
        hideFooter: false,
        noAnimatedTheme: true,
        hasGoogleReviews: false,
        breadcrumb,
        meta: {
          ...data.meta,
          title: `${data.meta.title !== 'MyArtBroker' ? data.meta.title : data.title} | MyArtBroker`,
        },
      },
    }
  } catch {
    return {
      notFound: true,
    }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      // Note: withLocalizedSettings will automatically merge localizedData into props
      // So we don't need to manually add settings, allArtists, totalArtists here
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export default Report
